'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, MarketId, MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import type { MarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { WrapSegment } from '@/components/common/WrapSegment';
import MarketActionReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketActionReviewDialogV2';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import { getRepayWithdrawOperationLabel } from '@/helpers/operationLabels';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { computeLtv, computePreviewFinalPosition } from '@/hooks/operation/market/v2/marketAction.helpers';
import { getMarketActionError } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketActionBuilder } from '@/hooks/operation/market/v2/useMarketActionBuilder';
import { useMarketBlue } from '@/hooks/operation/market/v2/useMarketBlue';
import { useMarketMaxWithdrawCollateralV2 } from '@/hooks/operation/market/v2/useMarketMaxWithdrawCollateralV2';
import { useMarketRepayNativeWrap } from '@/hooks/operation/market/v2/useMarketRepayNativeWrap';
import useAssetCanTransferOnchain from '@/hooks/operation/useAssetCanTransferOnchain';
import useBundleCanTransferOnchain, { isMarketBundleBlocked } from '@/hooks/operation/useBundleCanTransferOnchain';
import useFinalizeLabel from '@/hooks/operation/useFinalizeLabel';
import { useStickyMaxInput } from '@/hooks/useStickyMaxInput';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { MarketSummaryCard } from '../MarketSummary';

/** Debounce window (ms) before an edited amount drives the local build (validation only). */
const PREVIEW_DEBOUNCE_MS = 400;
interface MarketOperationRepayWithdrawProps {
  marketId: MarketId;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  lltv: BigIntish | undefined;
  /** morpho-sdk MarketParams for the v2 flow (from the parent's API market). */
  marketParams: MarketParams | undefined;
  marketForRewards?: MarketForRewards | null;
  avgBorrowApy?: number | null;
  /** VRM-5363: restricted-jurisdiction verdict for this market's assets; blocks the CTA. */
  geoRestriction: MarketGeoRestriction;
  /**
   * The panel's one-sided gate has not answered yet. The form still renders so typed input
   * survives, but it cannot be finalized: the wallet's borrow position is unconfirmed.
   */
  isPositionGatePending: boolean;
}
const MarketOperationRepayWithdraw = t0 => {
  const $ = _c(163);
  const {
    marketId,
    collateralAsset,
    loanAsset,
    lltv,
    marketParams,
    marketForRewards,
    avgBorrowApy,
    geoRestriction,
    isPositionGatePending
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const {
    toggleDialog
  } = useDialogContext();
  const {
    marketRepayWithdraw
  } = useFeatureFlags();
  const [inputCollateralAmount, setInputCollateralAmount] = useState(ZERO);
  const [inputLoanAmount, setInputLoanAmount] = useState(ZERO);
  const [isCollateralInputPending, setIsCollateralInputPending] = useState(false);
  const [isLoanInputPending, setIsLoanInputPending] = useState(false);
  const isMarketRepayWithdrawEnabled = marketRepayWithdraw && !!marketParams;
  let t1;
  if ($[0] !== isMarketRepayWithdrawEnabled || $[1] !== marketId || $[2] !== targetChain.id || $[3] !== targetChain.name) {
    t1 = {
      entryPoint: "market_page",
      action: "repay_withdraw",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      enabled: isMarketRepayWithdrawEnabled
    };
    $[0] = isMarketRepayWithdrawEnabled;
    $[1] = marketId;
    $[2] = targetChain.id;
    $[3] = targetChain.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useTrackMarketActionsV2EntryViewed(t1);
  const {
    positionData,
    error: positionError,
    isLoading: isPositionLoading
  } = useMarketBlue(marketParams);
  const debt = positionData?.borrowAssets;
  let t2;
  if ($[5] !== debt || $[6] !== inputLoanAmount || $[7] !== isMarketRepayWithdrawEnabled) {
    t2 = isMarketRepayWithdrawEnabled && isDefined(debt) && debt > ZERO && inputLoanAmount >= debt;
    $[5] = debt;
    $[6] = inputLoanAmount;
    $[7] = isMarketRepayWithdrawEnabled;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const isMaxRepay = t2;
  let t3;
  if ($[9] !== inputLoanAmount || $[10] !== isMarketRepayWithdrawEnabled || $[11] !== isMaxRepay || $[12] !== loanAsset.address || $[13] !== marketParams) {
    t3 = {
      marketParams,
      loanToken: loanAsset.address,
      totalRepay: inputLoanAmount,
      isMaxRepay,
      enabled: isMarketRepayWithdrawEnabled,
      wrapEnabled: isMarketRepayWithdrawEnabled
    };
    $[9] = inputLoanAmount;
    $[10] = isMarketRepayWithdrawEnabled;
    $[11] = isMaxRepay;
    $[12] = loanAsset.address;
    $[13] = marketParams;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const {
    isWNativeLoan,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    nativeAmount,
    maxRepay,
    isMaxLoading: isMaxRepayLoading,
    isWrappingToggleLoading,
    wrapErrorMessage
  } = useMarketRepayNativeWrap(t3);
  let t4;
  if ($[15] !== inputLoanAmount || $[16] !== isMarketRepayWithdrawEnabled || $[17] !== maxRepay) {
    t4 = isMarketRepayWithdrawEnabled && isDefined(maxRepay) && inputLoanAmount > maxRepay;
    $[15] = inputLoanAmount;
    $[16] = isMarketRepayWithdrawEnabled;
    $[17] = maxRepay;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const isRepayOverMax = t4;
  let t5;
  if ($[19] !== maxRepay) {
    t5 = {
      maxValue: maxRepay,
      onChange: setInputLoanAmount
    };
    $[19] = maxRepay;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const {
    isMaxActive: isRepayMaxActive,
    onMaxClick: onMaxRepayClick,
    onManualChange: setInputLoanAmountManually
  } = useStickyMaxInput(t5);
  let t6;
  if ($[21] !== inputLoanAmount || $[22] !== isMarketRepayWithdrawEnabled || $[23] !== isMaxRepay || $[24] !== marketParams) {
    t6 = {
      marketParams,
      proposedLoanRepay: inputLoanAmount,
      isMaxRepay,
      enabled: isMarketRepayWithdrawEnabled
    };
    $[21] = inputLoanAmount;
    $[22] = isMarketRepayWithdrawEnabled;
    $[23] = isMaxRepay;
    $[24] = marketParams;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const {
    maxWithdraw,
    maxWithdrawCeiling,
    isMaxLoading: isMaxWithdrawLoading
  } = useMarketMaxWithdrawCollateralV2(t6);
  let t7;
  if ($[26] !== maxWithdraw) {
    t7 = {
      maxValue: maxWithdraw,
      onChange: setInputCollateralAmount
    };
    $[26] = maxWithdraw;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const {
    isMaxActive: isWithdrawMaxActive,
    onMaxClick: onMaxWithdrawClick,
    onManualChange: setInputCollateralAmountManually
  } = useStickyMaxInput(t7);
  let t8;
  if ($[28] !== inputCollateralAmount || $[29] !== isMarketRepayWithdrawEnabled || $[30] !== maxWithdrawCeiling) {
    t8 = isMarketRepayWithdrawEnabled && isDefined(maxWithdrawCeiling) && inputCollateralAmount > maxWithdrawCeiling;
    $[28] = inputCollateralAmount;
    $[29] = isMarketRepayWithdrawEnabled;
    $[30] = maxWithdrawCeiling;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const isWithdrawOverMax = t8;
  const [debouncedCollateral] = useDebounce(inputCollateralAmount, PREVIEW_DEBOUNCE_MS);
  const [debouncedLoan] = useDebounce(inputLoanAmount, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = debouncedCollateral !== inputCollateralAmount || debouncedLoan !== inputLoanAmount;
  let t9;
  if ($[32] !== debouncedLoan || $[33] !== debt || $[34] !== isMarketRepayWithdrawEnabled) {
    t9 = isMarketRepayWithdrawEnabled && isDefined(debt) && debt > ZERO && debouncedLoan >= debt;
    $[32] = debouncedLoan;
    $[33] = debt;
    $[34] = isMarketRepayWithdrawEnabled;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const isMaxRepayDebounced = t9;
  let t10;
  if ($[36] !== debouncedCollateral || $[37] !== debouncedLoan || $[38] !== isMaxRepayDebounced || $[39] !== marketParams || $[40] !== nativeAmount) {
    t10 = {
      marketParams,
      mode: "repayWithdraw",
      collateralAmount: debouncedCollateral,
      loanAmount: debouncedLoan,
      isMaxRepay: isMaxRepayDebounced,
      nativeAmount,
      withRequirements: false
    };
    $[36] = debouncedCollateral;
    $[37] = debouncedLoan;
    $[38] = isMaxRepayDebounced;
    $[39] = marketParams;
    $[40] = nativeAmount;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const {
    buildError
  } = useMarketActionBuilder(t10);
  let t11;
  if ($[42] !== positionData) {
    t11 = positionData ? {
      collateral: positionData.collateral,
      borrowAssets: positionData.borrowAssets,
      price: positionData.market.price ?? ZERO
    } : undefined;
    $[42] = positionData;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  const current = t11;
  const isEmpty = inputCollateralAmount === ZERO && inputLoanAmount === ZERO;
  let t12;
  bb0: {
    if (isEmpty || !current) {
      let t13;
      if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {};
        $[44] = t13;
      } else {
        t13 = $[44];
      }
      t12 = t13;
      break bb0;
    }
    let t13;
    if ($[45] !== current || $[46] !== inputCollateralAmount || $[47] !== inputLoanAmount) {
      t13 = computePreviewFinalPosition({
        current,
        collateralDelta: inputCollateralAmount,
        loanDelta: inputLoanAmount,
        mode: "repayWithdraw"
      });
      $[45] = current;
      $[46] = inputCollateralAmount;
      $[47] = inputLoanAmount;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    t12 = t13;
  }
  const after = t12;
  let t13;
  bb1: {
    if (wrapErrorMessage) {
      t13 = wrapErrorMessage;
      break bb1;
    }
    if (isEmpty || isDebouncing) {
      t13 = undefined;
      break bb1;
    }
    let t14;
    if ($[49] !== buildError || $[50] !== debt || $[51] !== inputLoanAmount || $[52] !== isRepayOverMax || $[53] !== isWithdrawOverMax || $[54] !== loanAsset.symbol) {
      const exceedsLoan = isRepayOverMax && isDefined(debt) && inputLoanAmount > debt;
      t14 = getMarketActionError({
        buildError,
        insufficientBalanceSymbol: isRepayOverMax && !exceedsLoan ? loanAsset.symbol : undefined,
        exceedsLoan,
        exceedsBorrowCapacity: isWithdrawOverMax
      });
      $[49] = buildError;
      $[50] = debt;
      $[51] = inputLoanAmount;
      $[52] = isRepayOverMax;
      $[53] = isWithdrawOverMax;
      $[54] = loanAsset.symbol;
      $[55] = t14;
    } else {
      t14 = $[55];
    }
    t13 = t14;
  }
  const buildErrorMessage = t13;
  const canTransferLoanAsset = useAssetCanTransferOnchain(account.address, loanAsset.address, targetChain.id);
  const canTransferCollateralAsset = useAssetCanTransferOnchain(account.address, collateralAsset.address, targetChain.id);
  const isLoanTransferBlocked = inputLoanAmount > ZERO && !canTransferLoanAsset;
  const isCollateralTransferBlocked = inputCollateralAmount > ZERO && !canTransferCollateralAsset;
  const {
    canTransfer: loanBundleAuthorized
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset.address, targetChain.id);
  let t14;
  if ($[56] !== inputCollateralAmount || $[57] !== inputLoanAmount || $[58] !== loanBundleAuthorized) {
    t14 = isMarketBundleBlocked({
      legs: [{
        amount: inputLoanAmount,
        authorized: loanBundleAuthorized
      }, {
        amount: inputCollateralAmount,
        authorized: true
      }]
    });
    $[56] = inputCollateralAmount;
    $[57] = inputLoanAmount;
    $[58] = loanBundleAuthorized;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  const isBundleNotAuthorized = t14;
  const isFinalizeDisabled = !isMarketRepayWithdrawEnabled || isPositionGatePending || isEmpty || isPositionLoading || !!positionError || inputLoanAmount > ZERO && isMaxRepayLoading || isMaxWithdrawLoading || account.isWrongChain || !!buildErrorMessage || isRepayOverMax || isWithdrawOverMax || isLoanTransferBlocked || isCollateralTransferBlocked || isBundleNotAuthorized;
  let t15;
  if ($[60] !== inputCollateralAmount || $[61] !== inputLoanAmount) {
    t15 = getRepayWithdrawOperationLabel(inputCollateralAmount, inputLoanAmount);
    $[60] = inputCollateralAmount;
    $[61] = inputLoanAmount;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  const operationLabel = t15;
  let t16;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = [];
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== account.isWrongChain || $[65] !== buildErrorMessage || $[66] !== isEmpty || $[67] !== operationLabel) {
    t17 = {
      operationLabel,
      isInitialState: isEmpty,
      isNew: true,
      isEditing: false,
      localErrorMessage: buildErrorMessage,
      operations: t16,
      isPending: false,
      blockError: null,
      isWrongChain: account.isWrongChain,
      hasDisabledMessage: false,
      noReviewPrefix: true
    };
    $[64] = account.isWrongChain;
    $[65] = buildErrorMessage;
    $[66] = isEmpty;
    $[67] = operationLabel;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  const finalizeLabel = useFinalizeLabel(t17);
  const isButtonLoading = account.isConnected && !positionError && isPositionLoading;
  const tooltipContent = useMarketTooltip(buildErrorMessage);
  let t18;
  if ($[69] !== finalizeLabel || $[70] !== geoRestriction || $[71] !== isButtonLoading || $[72] !== isFinalizeDisabled || $[73] !== tooltipContent) {
    t18 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled,
      isLoading: isButtonLoading,
      finalizeLabel,
      tooltipContent
    });
    $[69] = finalizeLabel;
    $[70] = geoRestriction;
    $[71] = isButtonLoading;
    $[72] = isFinalizeDisabled;
    $[73] = tooltipContent;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[75] !== t18) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t18);
    $[75] = t18;
    $[76] = geoActionProps;
    $[77] = shouldBlockFinalize;
  } else {
    geoActionProps = $[76];
    shouldBlockFinalize = $[77];
  }
  let t19;
  if ($[78] !== avgBorrowApy || $[79] !== collateralAsset || $[80] !== inputCollateralAmount || $[81] !== inputLoanAmount || $[82] !== isMaxRepay || $[83] !== loanAsset || $[84] !== marketForRewards || $[85] !== marketId || $[86] !== marketParams || $[87] !== nativeAmount || $[88] !== shouldBlockFinalize || $[89] !== targetChain.id || $[90] !== targetChain.name || $[91] !== toggleDialog) {
    t19 = () => {
      if (!marketParams || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "market_page",
        action: "repay_withdraw",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name
      });
      toggleDialog(<MarketActionReviewDialogV2 marketParams={marketParams} marketId={marketId} mode="repayWithdraw" collateralAmount={inputCollateralAmount} loanAmount={inputLoanAmount} nativeAmount={nativeAmount} isMaxRepay={isMaxRepay} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={marketParams.lltv} marketForRewards={marketForRewards} avgBorrowApy={avgBorrowApy} onTxSuccess={() => {
        setInputLoanAmount(ZERO);
        setInputCollateralAmount(ZERO);
      }} entryPoint="market_page" />);
    };
    $[78] = avgBorrowApy;
    $[79] = collateralAsset;
    $[80] = inputCollateralAmount;
    $[81] = inputLoanAmount;
    $[82] = isMaxRepay;
    $[83] = loanAsset;
    $[84] = marketForRewards;
    $[85] = marketId;
    $[86] = marketParams;
    $[87] = nativeAmount;
    $[88] = shouldBlockFinalize;
    $[89] = targetChain.id;
    $[90] = targetChain.name;
    $[91] = toggleDialog;
    $[92] = t19;
  } else {
    t19 = $[92];
  }
  const handleFinalize = t19;
  if (positionError && !positionData) {
    let t20;
    if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <ErrorServiceUnavailable minHeight="600px" />;
      $[93] = t20;
    } else {
      t20 = $[93];
    }
    return t20;
  }
  const t20 = `Repay Loan ${loanAsset.symbol}`;
  let t21;
  if ($[94] !== inputLoanAmount || $[95] !== isMaxRepayLoading || $[96] !== isRepayMaxActive || $[97] !== loanAsset || $[98] !== maxRepay || $[99] !== onMaxRepayClick || $[100] !== setInputLoanAmountManually || $[101] !== t20) {
    t21 = <LargeAssetInput header={t20} token={loanAsset} amount={inputLoanAmount} onChange={setInputLoanAmountManually} onPendingChange={setIsLoanInputPending} maxValue={maxRepay} isMaxLoading={isMaxRepayLoading} isMax={isRepayMaxActive} onMaxClick={onMaxRepayClick} autoFocus={true} />;
    $[94] = inputLoanAmount;
    $[95] = isMaxRepayLoading;
    $[96] = isRepayMaxActive;
    $[97] = loanAsset;
    $[98] = maxRepay;
    $[99] = onMaxRepayClick;
    $[100] = setInputLoanAmountManually;
    $[101] = t20;
    $[102] = t21;
  } else {
    t21 = $[102];
  }
  let t22;
  if ($[103] !== isMarketRepayWithdrawEnabled || $[104] !== isWNativeLoan || $[105] !== isWrapping || $[106] !== isWrappingToggleLoading || $[107] !== loanAsset.symbol || $[108] !== onWrappingChange || $[109] !== targetChain.nativeCurrency.symbol || $[110] !== wrappableNativeBalance) {
    t22 = <WrapSegment id="market-repay" enabled={isMarketRepayWithdrawEnabled} isWNative={isWNativeLoan} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={loanAsset.symbol} nativeSymbol={targetChain.nativeCurrency.symbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[103] = isMarketRepayWithdrawEnabled;
    $[104] = isWNativeLoan;
    $[105] = isWrapping;
    $[106] = isWrappingToggleLoading;
    $[107] = loanAsset.symbol;
    $[108] = onWrappingChange;
    $[109] = targetChain.nativeCurrency.symbol;
    $[110] = wrappableNativeBalance;
    $[111] = t22;
  } else {
    t22 = $[111];
  }
  let t23;
  if ($[112] !== isLoanTransferBlocked || $[113] !== loanAsset.address) {
    t23 = isLoanTransferBlocked && <TransferError tokenAddress={loanAsset.address} />;
    $[112] = isLoanTransferBlocked;
    $[113] = loanAsset.address;
    $[114] = t23;
  } else {
    t23 = $[114];
  }
  let t24;
  if ($[115] !== t21 || $[116] !== t22 || $[117] !== t23) {
    t24 = <div className="flex flex-col gap-[2px]">{t21}{t22}{t23}</div>;
    $[115] = t21;
    $[116] = t22;
    $[117] = t23;
    $[118] = t24;
  } else {
    t24 = $[118];
  }
  const t25 = `Withdraw Collateral ${collateralAsset.symbol}`;
  let t26;
  if ($[119] !== collateralAsset || $[120] !== inputCollateralAmount || $[121] !== isMaxWithdrawLoading || $[122] !== isWithdrawMaxActive || $[123] !== maxWithdraw || $[124] !== onMaxWithdrawClick || $[125] !== setInputCollateralAmountManually || $[126] !== t25) {
    t26 = <LargeAssetInput header={t25} token={collateralAsset} amount={inputCollateralAmount} onChange={setInputCollateralAmountManually} onPendingChange={setIsCollateralInputPending} maxValue={maxWithdraw} isMaxLoading={isMaxWithdrawLoading} isMax={isWithdrawMaxActive} onMaxClick={onMaxWithdrawClick} />;
    $[119] = collateralAsset;
    $[120] = inputCollateralAmount;
    $[121] = isMaxWithdrawLoading;
    $[122] = isWithdrawMaxActive;
    $[123] = maxWithdraw;
    $[124] = onMaxWithdrawClick;
    $[125] = setInputCollateralAmountManually;
    $[126] = t25;
    $[127] = t26;
  } else {
    t26 = $[127];
  }
  let t27;
  if ($[128] !== collateralAsset.address || $[129] !== isCollateralTransferBlocked) {
    t27 = isCollateralTransferBlocked && <TransferError tokenAddress={collateralAsset.address} />;
    $[128] = collateralAsset.address;
    $[129] = isCollateralTransferBlocked;
    $[130] = t27;
  } else {
    t27 = $[130];
  }
  let t28;
  if ($[131] !== t26 || $[132] !== t27) {
    t28 = <div className="flex flex-col gap-[2px]">{t26}{t27}</div>;
    $[131] = t26;
    $[132] = t27;
    $[133] = t28;
  } else {
    t28 = $[133];
  }
  const t29 = current?.collateral;
  const t30 = current?.borrowAssets;
  let t31;
  if ($[134] !== current) {
    t31 = current ? computeLtv({
      collateral: current.collateral,
      borrowAssets: current.borrowAssets,
      price: current.price
    }) : undefined;
    $[134] = current;
    $[135] = t31;
  } else {
    t31 = $[135];
  }
  let t32;
  if ($[136] !== t29 || $[137] !== t30 || $[138] !== t31) {
    t32 = {
      collateral: t29,
      loan: t30,
      ltv: t31
    };
    $[136] = t29;
    $[137] = t30;
    $[138] = t31;
    $[139] = t32;
  } else {
    t32 = $[139];
  }
  let t33;
  if ($[140] !== after || $[141] !== avgBorrowApy || $[142] !== collateralAsset || $[143] !== isPositionLoading || $[144] !== lltv || $[145] !== loanAsset || $[146] !== marketForRewards || $[147] !== marketId || $[148] !== t32) {
    t33 = <MarketSummaryCard marketId={marketId} current={t32} after={after} collateralAsset={collateralAsset} loanAsset={loanAsset} lltv={lltv} isLoading={isPositionLoading} marketForRewards={marketForRewards} avgBorrowApy={avgBorrowApy} />;
    $[140] = after;
    $[141] = avgBorrowApy;
    $[142] = collateralAsset;
    $[143] = isPositionLoading;
    $[144] = lltv;
    $[145] = loanAsset;
    $[146] = marketForRewards;
    $[147] = marketId;
    $[148] = t32;
    $[149] = t33;
  } else {
    t33 = $[149];
  }
  let t34;
  if ($[150] !== isBundleNotAuthorized) {
    t34 = isBundleNotAuthorized && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[150] = isBundleNotAuthorized;
    $[151] = t34;
  } else {
    t34 = $[151];
  }
  const t35 = isCollateralInputPending || isLoanInputPending;
  let t36;
  if ($[152] !== geoActionProps || $[153] !== handleFinalize || $[154] !== isDebouncing || $[155] !== t35) {
    t36 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={t35} isValidating={isDebouncing} />;
    $[152] = geoActionProps;
    $[153] = handleFinalize;
    $[154] = isDebouncing;
    $[155] = t35;
    $[156] = t36;
  } else {
    t36 = $[156];
  }
  let t37;
  if ($[157] !== t24 || $[158] !== t28 || $[159] !== t33 || $[160] !== t34 || $[161] !== t36) {
    t37 = <>{t24}{t28}{t33}{t34}{t36}</>;
    $[157] = t24;
    $[158] = t28;
    $[159] = t33;
    $[160] = t34;
    $[161] = t36;
    $[162] = t37;
  } else {
    t37 = $[162];
  }
  return t37;
};
export default memo(MarketOperationRepayWithdraw);